import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { X, Loader2, Type, Check, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { composeHtmlWithBg } from '../../utils/composeHtmlBg';

const API = process.env.REACT_APP_BACKEND_URL;
const SLIDE_W = 1080;
const SLIDE_H = 1350;

/**
 * Extract editable text nodes from a page's HTML.
 *
 * Strategy: Use DOM TreeWalker to visit EVERY non-whitespace text node
 * (skipping <style>/<script>).  Each text node becomes its own editable
 * entry — this correctly captures mixed content like:
 *
 *   <div class="text">你記下了錢的去向<span class="mute">卻沒問它為什麼要去</span></div>
 *
 * where the older element-based extractor missed the parent's direct text.
 *
 * We keep a live reference to the underlying `textNode` so edits mutate the
 * DOM in-place; serializing the parsed document then produces the updated
 * HTML string.
 *
 * Returns:
 *   { doc, entries: [{ key, tagName, fontSizePx, text, textNode }] }
 */
function extractEditableTexts(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html || '', 'text/html');
  const entries = [];
  const skipTags = new Set(['STYLE', 'SCRIPT', 'HEAD', 'META', 'LINK', 'TITLE', 'NOSCRIPT']);

  if (!doc.body) return { doc, entries };

  // TreeWalker: yields text nodes only, filter rejects nodes inside skipTags
  // ancestors and whitespace-only content.
  // NodeFilter.SHOW_TEXT === 4
  const walker = doc.createTreeWalker(doc.body, 4, {
    acceptNode(node) {
      const text = (node.textContent || '');
      if (!text.trim()) return 3; // FILTER_REJECT
      // Walk up parents to reject anything inside skipTags
      let p = node.parentElement;
      while (p) {
        if (skipTags.has(p.tagName)) return 3;
        p = p.parentElement;
      }
      return 1; // FILTER_ACCEPT
    },
  });

  let idx = 0;
  while (walker.nextNode()) {
    const textNode = walker.currentNode;
    const trimmed = (textNode.textContent || '').trim();
    if (!trimmed) continue;
    if (trimmed.length > 500) continue; // sanity cap

    // Font-size heuristic — parent's inline style + walk up to catch CSS
    // classes that resolve via injected <style>.  Since DOMParser doesn't
    // apply stylesheets, we do a best-effort by scanning inline styles.
    const parent = textNode.parentElement;
    let fontSizePx = 16;
    let cursor = parent;
    while (cursor) {
      const inline = cursor.getAttribute && cursor.getAttribute('style');
      if (inline) {
        const m = /font-size:\s*(\d+(?:\.\d+)?)\s*(px|rem|em|vh|vw)?/i.exec(inline);
        if (m) {
          const num = parseFloat(m[1]);
          const unit = (m[2] || 'px').toLowerCase();
          if (unit === 'px') fontSizePx = num;
          else if (unit === 'rem' || unit === 'em') fontSizePx = num * 16;
          else if (unit === 'vh') fontSizePx = num * 13.5; // approx (1350/100)
          else if (unit === 'vw') fontSizePx = num * 10.8; // approx (1080/100)
          break;
        }
      }
      cursor = cursor.parentElement;
    }
    // Also try to read font-size from any <style> block by matching the
    // element's class(es) to `.class { font-size: X }` rules.  Cheap: only
    // do this if inline style didn't set anything.
    if (fontSizePx === 16 && parent && parent.className) {
      const classes = String(parent.className).split(/\s+/).filter(Boolean);
      if (classes.length && doc.head) {
        const styleBlocks = doc.head.querySelectorAll('style');
        for (const sb of styleBlocks) {
          const css = sb.textContent || '';
          for (const cls of classes) {
            const re = new RegExp(
              `\\.${cls.replace(/[-.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\s*\\{[^}]*font-size:\\s*(\\d+(?:\\.\\d+)?)\\s*(px|rem|em|vh|vw)?`,
              'i',
            );
            const m = re.exec(css);
            if (m) {
              const num = parseFloat(m[1]);
              const unit = (m[2] || 'px').toLowerCase();
              if (unit === 'px') fontSizePx = num;
              else if (unit === 'rem' || unit === 'em') fontSizePx = num * 16;
              else if (unit === 'vh') fontSizePx = num * 13.5;
              else if (unit === 'vw') fontSizePx = num * 10.8;
              break;
            }
          }
          if (fontSizePx !== 16) break;
        }
      }
    }

    entries.push({
      key: `t-${idx++}`,
      tagName: parent ? parent.tagName : 'TEXT',
      className: parent && parent.className ? String(parent.className).slice(0, 40) : '',
      fontSizePx,
      text: trimmed,
      textNode,
    });
  }

  return { doc, entries };
}

/**
 * Serialize a DOMParser Document back to a full HTML string with doctype
 * preserved. Compatible with the srcDoc iframe.
 */
function serializeDoc(doc) {
  if (!doc || !doc.documentElement) return '';
  const doctype = '<!DOCTYPE html>';
  return doctype + doc.documentElement.outerHTML;
}

/**
 * Debounce hook — fires callback after `delay` ms of no new calls.
 */
function useDebouncedCallback(cb, delay) {
  const timer = useRef(null);
  return (...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => cb(...args), delay);
  };
}

/**
 * InlineTextEditModal
 *
 * Left column: live iframe preview of the slide.
 * Right column: editable list of text fields extracted from HTML.
 *
 * Every keystroke:
 *   1. Mutates the parsed DOM tree
 *   2. Re-serialises to a new HTML string
 *   3. Updates local iframe (instant preview via srcDoc React re-render)
 *   4. Debounced (600ms) PUT to /api/carousel/{id}/page/{index}/html
 *
 * On close, if there are unsaved changes the modal explicitly saves before
 * closing to prevent data loss.
 */
export default function InlineTextEditModal({
  open,
  carouselId,
  page,
  onClose,
  onPageUpdate,
}) {
  const [parsedHtml, setParsedHtml] = useState(null); // The Document object
  const [entries, setEntries] = useState([]); // Editable text entries
  const [currentHtml, setCurrentHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const originalHtmlRef = useRef('');

  // Initialize on open / page-INDEX change.
  //
  // We intentionally do NOT include `page?.html` as a dependency: the modal
  // owns its own parsed DOM tree for the entire editing session.  If we
  // re-parsed on every html change, then whenever the user emptied a
  // textarea (`target.textContent = ""`), DOMParser would drop the empty
  // text node → TreeWalker would find no node → the entry would vanish
  // from the list and the user could no longer type it back.
  //
  // By parsing once and holding the doc + textNode refs alive, users can
  // freely empty and re-fill any field.  Fresh parses only happen when the
  // user opens a different slide or clicks 還原 (which re-parses the
  // pristine originalHtmlRef snapshot).
  useEffect(() => {
    if (!open || !page?.html) return;
    const { doc, entries: e } = extractEditableTexts(page.html);
    setParsedHtml(doc);
    setEntries(e);
    setCurrentHtml(page.html);
    setDirty(false);
    originalHtmlRef.current = page.html;
  }, [open, page?.index]);

  const debouncedSave = useDebouncedCallback(async (htmlToSave) => {
    if (!carouselId || page?.index == null) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/carousel/${carouselId}/page/${page.index}/html`,
        { html: htmlToSave },
        { timeout: 15000 },
      );
      setDirty(false);
    } catch (err) {
      toast.error('儲存失敗：' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  }, 600);

  const handleEdit = (entryIndex, newText) => {
    if (!parsedHtml) return;
    // Mutate the underlying text node in-place — this is the granular
    // per-text-node swap enabled by the TreeWalker-based extractor.  Even
    // for mixed content like `<div>直接文字<span>span 文字</span></div>`
    // each fragment edits independently.
    const target = entries[entryIndex]?.textNode;
    if (!target) return;
    target.textContent = newText;

    // Re-serialise
    const newHtml = serializeDoc(parsedHtml);
    setCurrentHtml(newHtml);
    setEntries((prev) =>
      prev.map((e, i) => (i === entryIndex ? { ...e, text: newText } : e)),
    );
    setDirty(true);
    // Bubble up so parent updates its page state → iframe re-renders in the grid too
    if (onPageUpdate) onPageUpdate(page.index, newHtml);
    debouncedSave(newHtml);
  };

  const handleRevert = () => {
    if (!originalHtmlRef.current) return;
    if (!window.confirm('所有改動會被還原到打開編輯器時的版本，是否繼續？')) return;
    const { doc, entries: e } = extractEditableTexts(originalHtmlRef.current);
    setParsedHtml(doc);
    setEntries(e);
    setCurrentHtml(originalHtmlRef.current);
    setDirty(true);
    if (onPageUpdate) onPageUpdate(page.index, originalHtmlRef.current);
    debouncedSave(originalHtmlRef.current);
  };

  const composedPreview = useMemo(
    () => composeHtmlWithBg(currentHtml, page?.bg_image),
    [currentHtml, page?.bg_image],
  );

  if (!open || !page) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={() => {
        if (dirty) {
          if (!window.confirm('尚有改動未儲存，確定要關閉嗎？')) return;
        }
        onClose();
      }}
      data-testid="inline-text-edit-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-6xl max-h-[95vh] flex flex-col rounded-2xl border border-obsidian-600 bg-obsidian-950 shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-obsidian-700 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-lime-500/15 flex items-center justify-center text-lime-400">
              <Type className="w-4 h-4" />
            </div>
            <div>
              <p className="label-micro text-lime-400">直接編輯文字 · 第 {page.index} 頁</p>
              <h3 className="font-display text-white text-sm">即時編輯文字，即時查看效果</h3>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {saving && (
              <span className="flex items-center gap-1.5 text-[11px] text-obsidian-400">
                <Loader2 className="w-3 h-3 animate-spin" /> 儲存中
              </span>
            )}
            {!saving && !dirty && (
              <span className="flex items-center gap-1.5 text-[11px] text-lime-400">
                <Check className="w-3 h-3" /> 已儲存
              </span>
            )}
            <button
              onClick={handleRevert}
              className="flex items-center gap-1 px-2 py-1 text-[11px] text-obsidian-400 hover:text-lime-400 transition-colors"
              data-testid="inline-text-edit-revert"
            >
              <Undo2 className="w-3 h-3" /> 還原
            </button>
            <button
              onClick={() => {
                if (dirty && !window.confirm('尚有改動未儲存，確定要關閉嗎？')) return;
                onClose();
              }}
              className="text-obsidian-400 hover:text-white p-1"
              data-testid="inline-text-edit-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body: two columns */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 p-5 overflow-hidden">
          {/* LEFT: Live preview */}
          <div className="flex items-start justify-center min-h-0">
            <div
              className="relative rounded-xl overflow-hidden border border-obsidian-600 bg-white shadow-lg w-full"
              style={{ aspectRatio: `${SLIDE_W}/${SLIDE_H}`, maxHeight: '82vh' }}
            >
              <iframe
                key={page.index /* remount when switching slides */}
                srcDoc={composedPreview}
                sandbox="allow-same-origin"
                title={`inline-edit-p${page.index}`}
                scrolling="no"
                style={{
                  width: `${SLIDE_W}px`,
                  height: `${SLIDE_H}px`,
                  border: 0,
                  transform: 'scale(0.42)',
                  transformOrigin: 'top left',
                  pointerEvents: 'none',
                  backgroundColor: '#ffffff',
                }}
              />
            </div>
          </div>

          {/* RIGHT: Text fields */}
          <div className="flex flex-col min-h-0">
            <p className="text-[11px] text-obsidian-400 mb-2 shrink-0">
              🖊️ 輸入文字後，左邊會即時更新 · 每個欄位對應貼文上的一段文字（依字體大小排序，大字排在前）
            </p>
            <div className="flex-1 overflow-y-auto pr-1 space-y-3">
              {entries.length === 0 && (
                <p className="text-obsidian-400 text-sm py-8 text-center">
                  此頁沒有可編輯的文字。可嘗試用 AI 對話修改內容。
                </p>
              )}
              {entries.map((entry, i) => {
                const sizeLabel =
                  entry.fontSizePx >= 60
                    ? '主標題'
                    : entry.fontSizePx >= 40
                      ? '大標題'
                      : entry.fontSizePx >= 28
                        ? '中標題'
                        : entry.fontSizePx >= 20
                          ? '小標題'
                          : '內文';
                return (
                  <div
                    key={entry.key}
                    className="rounded-lg border border-obsidian-700 bg-obsidian-900/40 p-3"
                    data-testid={`inline-text-field-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[10px] text-lime-400 tabular-nums">
                        {sizeLabel}
                      </span>
                      <span className="text-[10px] text-obsidian-500">
                        {Math.round(entry.fontSizePx)}px
                      </span>
                    </div>
                    <textarea
                      value={entry.text}
                      onChange={(e) => handleEdit(i, e.target.value)}
                      rows={Math.max(1, Math.min(4, Math.ceil(entry.text.length / 30)))}
                      className="w-full bg-obsidian-950 border border-obsidian-700 focus:border-lime-500 outline-none text-white text-sm rounded-md px-2 py-1.5 resize-none font-serif"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-obsidian-700 flex items-center justify-between shrink-0">
          <p className="text-[11px] text-obsidian-500">
            💡 這個編輯器不使用 AI，你輸入的文字會原封不動地儲存
          </p>
          <button
            onClick={() => {
              // Force flush any pending debounce
              if (dirty) {
                (async () => {
                  setSaving(true);
                  try {
                    await axios.put(
                      `${API}/api/carousel/${carouselId}/page/${page.index}/html`,
                      { html: currentHtml },
                      { timeout: 15000 },
                    );
                    setDirty(false);
                    toast.success(`第 ${page.index} 頁已儲存`);
                  } catch (err) {
                    toast.error('儲存失敗：' + (err.response?.data?.detail || err.message));
                  } finally {
                    setSaving(false);
                    onClose();
                  }
                })();
              } else {
                onClose();
              }
            }}
            className="h-9 px-4 rounded-lg bg-lime-500 hover:bg-lime-400 text-obsidian-950 text-sm font-semibold transition-colors"
            data-testid="inline-text-edit-done"
          >
            完成
          </button>
        </div>
      </div>
    </div>
  );
}
